/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionnaireSectionProps {
  id: string;
  name: string;
  questionnaireId: string;
  sequence: number;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionnaireSectionRecord extends Record<QuestionnaireSectionProps>({
  id: null as any,
  name: null as any,
  questionnaireId: null as any,
  sequence: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    questionnaireId: string;
    sequence: number;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionnaireSectionRecord {
    if (json == null) {
      throw new Error('QuestionnaireSectionRecord.fromJS: parameter cannot be null');
    }
    return new QuestionnaireSectionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireSectionJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'QuestionnaireSectionJson.name'),
      questionnaireId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'questionnaireId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireSectionJson.questionnaireId',
      ),
      sequence: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'sequence', 0),
        'QuestionnaireSectionJson.sequence',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    questionnaireId: string;
    sequence: number;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionnaireSectionRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionnaireSectionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionnaireSectionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireSectionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'QuestionnaireSectionJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'questionnaireId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireSectionJson.questionnaireId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sequence: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'sequence', 0),
            'QuestionnaireSectionJson.sequence',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionnaireSectionProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionnaireSectionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionnaireSectionViewModelBase implements QuestionnaireSectionProps {
  protected readonly _props: QuestionnaireSectionProps;
  private _json?: QuestionnaireSectionProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get questionnaireId(): string {
    return this._props.questionnaireId;
  }
  get sequence(): number {
    return this._props.sequence;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: QuestionnaireSectionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        questionnaireId: this._props.questionnaireId,
        sequence: this._props.sequence,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionnaireSectionEntities = (state: AppState) =>
  state.entityStores.questionnaireSection.entities;

export const selectQuestionnaireSectionById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireSection.entities.get(id);

export const selectQuestionnaireSectionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireSection.meta.get(id);

export const selectQuestionnaireSectionAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionnaireSectionById(id),
    selectQuestionnaireSectionMetadataById(id),
    (questionnaireSection, meta) => ({ questionnaireSection, meta }),
  );

export function selectQuestionnaireSectionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionnaireSectionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionnaireSectionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionnaireSectionEntitiesByName(name: string) {
  return createSelector(selectAllQuestionnaireSectionEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectQuestionnaireSectionEntitiesByQuestionnaireId(questionnaireId: string) {
  return createSelector(selectAllQuestionnaireSectionEntities, (entities) =>
    entities.filter((e) => e.questionnaireId === questionnaireId).valueSeq(),
  );
}

export function selectQuestionnaireSectionEntitiesBySequence(sequence: number) {
  return createSelector(selectAllQuestionnaireSectionEntities, (entities) =>
    entities.filter((e) => e.sequence === sequence).valueSeq(),
  );
}
//#endregion Selectors
